import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { OrbitMotif } from "@/components/OrbitMotif";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { extractErrorCode } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, login, register } = useAuth();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState(refCode);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === "signup") {
        await register(email, password, name, referralCode || undefined);
        toast.success("Account created");
      } else {
        await login(email, password);
        toast.success("Welcome back");
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const code = extractErrorCode(err);
      setError(code);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen bg-white" data-testid="auth-page">
      {/* Left: form */}
      <div className="flex w-full flex-col px-6 py-8 lg:w-1/2 lg:px-16">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="auth-back-home">
            <Logo size="md" />
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center">
          <h1
            className="font-display text-4xl font-extrabold tracking-tight text-[#05073B]"
            data-testid="auth-title"
          >
            {mode === "signup" ? t("auth.welcome_new") : t("auth.welcome_back")}
          </h1>

          {error && (
            <div
              className="mt-6 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              data-testid="auth-error"
            >
              {t(`auth.err_${error}`)}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="auth-form">
            {mode === "signup" && (
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                  {t("auth.name_label")}
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="auth-name-input"
                  className="mt-1.5 h-11 rounded-lg"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                {t("auth.email_label")}
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email-input"
                className="mt-1.5 h-11 rounded-lg"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                {t("auth.password_label")}
              </Label>
              <Input
                id="password"
                type="password"
                required
                minLength={8}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password-input"
                className="mt-1.5 h-11 rounded-lg"
              />
              {mode === "signup" && (
                <p className="mt-1.5 text-xs text-slate-500">{t("auth.password_hint")}</p>
              )}
            </div>
            {mode === "signup" && (
              <div>
                <Label htmlFor="referral" className="text-sm font-medium text-slate-700">
                  Referral code <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <Input
                  id="referral"
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  maxLength={12}
                  data-testid="auth-referral-input"
                  className="mt-1.5 h-11 rounded-lg font-mono uppercase"
                  placeholder="ABC12345"
                />
                {refCode && (
                  <p className="mt-1.5 text-xs text-[#7AAA20]">
                    ✓ You were invited — your friend will earn a free month when you upgrade.
                  </p>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="lime-glow h-12 w-full rounded-full bg-[#9ACD32] text-base font-bold text-[#05073B] hover:bg-[#A8DF38]"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : mode === "signup" ? (
                t("auth.sign_up_btn")
              ) : (
                t("auth.sign_in_btn")
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs uppercase tracking-[0.15em] text-slate-400">
                {t("auth.or_divider")}
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            className="h-12 w-full rounded-full border-slate-300 text-sm font-semibold"
            data-testid="auth-google-btn"
          >
            <GoogleIcon className="mr-2 h-5 w-5" />
            {t("auth.google_btn")}
          </Button>

          <p className="mt-8 text-center text-sm text-slate-600">
            {mode === "signup" ? t("auth.have_account") : t("auth.no_account")}{" "}
            <button
              type="button"
              onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
              className="font-semibold text-[#0B0E5C] underline-offset-4 hover:underline"
              data-testid="auth-toggle-mode"
            >
              {mode === "signup" ? t("auth.sign_in_btn") : t("auth.sign_up_btn")}
            </button>
          </p>
        </div>
      </div>

      {/* Right: brand panel */}
      <div className="relative hidden overflow-hidden bg-[#05073B] lg:flex lg:w-1/2 lg:flex-col lg:justify-center lg:px-16">
        <OrbitMotif
          className="absolute -right-40 -top-40 h-[800px] w-[800px] orbit-anim"
          color="#9ACD32"
          opacity={0.18}
        />
        <OrbitMotif
          className="absolute -bottom-40 -left-40 h-[600px] w-[600px]"
          color="#1A1D7A"
          opacity={0.4}
        />

        <div className="relative max-w-md">
          <Logo size="xl" inverted />
          <h2 className="mt-12 font-display text-4xl font-extrabold leading-tight tracking-tight text-white">
            One platform.<br />
            <span className="text-[#9ACD32]">Three models.</span>
            <br />
            Infinite ideas.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-slate-300">
            Claude Sonnet 4.5 · GPT-5.2 · Gemini 3 Flash<br />
            + Image generation · Code Builder
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" {...props}>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.1A6.61 6.61 0 0 1 5.49 12c0-.73.13-1.44.35-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l3.66-2.83Z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38Z"
        fill="#EA4335"
      />
    </svg>
  );
}
